import { apiGet } from './api';
import type { ConnectionStatus, SavedHub } from './types';
import { renderConnect } from './pages/connect';
import { renderHome } from './pages/home';
import { renderApps } from './pages/apps';
import { renderHubSwitcher } from './pages/hub-switcher';
import { renderSettings } from './pages/settings';
import { renderBottomNav } from './components/bottom-nav';
import { renderConnectionBar } from './components/connection-bar';

type Page = 'connect' | 'home' | 'apps' | 'switcher' | 'settings';

let currentPage: Page = 'home';

const root = document.getElementById('app');
if (!root) throw new Error('missing #app element');

/** Main entry — check connection state and render. */
async function init(): Promise<void> {
  // Detect iframe context — hide header if embedded
  if (window.self !== window.top) {
    document.body.classList.add('embedded');
  }

  try {
    const hubs = await apiGet<SavedHub[]>('/api/hubs');
    if (hubs.length === 0) {
      currentPage = 'connect';
    } else {
      const status = await apiGet<ConnectionStatus>('/api/status');
      if (status.connection_state === 'offline') {
        currentPage = 'home'; // show cached
      }
    }
  } catch {
    currentPage = 'connect';
  }

  render();

  // Standalone banner
  void checkStandalone();
}

async function checkStandalone(): Promise<void> {
  if (sessionStorage.getItem('banner-dismissed')) return;
  try {
    const res = await fetch('../hub-registry.json');
    if (!res.ok || !res.headers.get('content-type')?.includes('json')) {
      showBanner();
      return;
    }
    const reg = await res.json();
    const hasCore = reg && Object.values(reg).some((e: unknown) => (e as {id?:string})?.id === 'hub-core');
    if (!hasCore) showBanner();
  } catch {
    showBanner();
  }
}

function showBanner(): void {
  const el = document.getElementById('standalone-banner');
  if (!el) return;
  el.style.display = 'flex';
}

/** Render the current page with chrome (connection bar + bottom nav). */
function render(): void {
  if (!root) return;

  root.innerHTML = '';

  // Connection bar (top)
  if (currentPage !== 'connect') {
    const bar = renderConnectionBar();
    root.appendChild(bar);
  }

  // Main content
  const main = document.createElement('main');
  main.className = 'page-content';

  switch (currentPage) {
    case 'connect':
      renderConnect(main, () => navigate('home'));
      break;
    case 'home':
      renderHome(main);
      break;
    case 'apps':
      renderApps(main);
      break;
    case 'switcher':
      renderHubSwitcher(main);
      break;
    case 'settings':
      renderSettings(main);
      break;
  }

  root.appendChild(main);

  // Bottom nav (except connect page)
  if (currentPage !== 'connect') {
    const nav = renderBottomNav(currentPage, navigate);
    root.appendChild(nav);
  }
}

/** Navigate to a page. */
export function navigate(page: Page): void {
  currentPage = page;
  render();
}

init();
